import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export interface Product {
  id: string;
  nome: string;
  descricao: string | null;
  preco: number;
  imagem_url: string | null;
  categoria_id: string | null;
  tipo: string;
  rating?: number;
  reviewCount?: number;
}

// Public interface with limited data for unauthenticated users
export interface PublicProduct {
  id: string;
  nome: string;
  categoria_id: string | null;
  tipo: string;
  rating?: number;
  reviewCount?: number;
}

export const useProducts = () => {
  const { user } = useAuthContext();
  
  return useQuery({
    queryKey: ["products", user ? "authenticated" : "public"],
    queryFn: async () => {
      if (user) {
        // Authenticated users get full product information
        const { data, error } = await supabase
          .from("produtos")
          .select(`
            id,
            nome,
            descricao,
            preco,
            imagem_url,
            categoria_id,
            tipo,
            categorias (
              nome
            )
          `)
          .eq("aprovado", true)
          .eq("ativo", true)
          .order("criado_em", { ascending: false });

        if (error) {
          console.error("Erro ao buscar produtos:", error);
          throw error;
        }

        return data as Product[];
      } else {
        // Unauthenticated users get limited information to prevent business intelligence exposure
        const { data, error } = await supabase
          .from("produtos")
          .select(`
            id,
            nome,
            categoria_id,
            tipo,
            categorias (
              nome
            )
          `)
          .eq("aprovado", true)
          .eq("ativo", true)
          .order("criado_em", { ascending: false });

        if (error) {
          console.error("Erro ao buscar produtos:", error);
          throw error;
        }

        // Transform to public interface
        return data.map(item => ({
          ...item,
          descricao: null,
          preco: 0, // Hidden from public
          imagem_url: null,
          rating: undefined,
          reviewCount: undefined
        })) as Product[];
      }
    },
  });
};

export const useProductsByCategory = (categoryId?: string) => {
  const { user } = useAuthContext();
  
  return useQuery({
    queryKey: ["products", "category", categoryId, user ? "authenticated" : "public"],
    queryFn: async () => {
      if (user) {
        // Authenticated users get full product information
        let query = supabase
          .from("produtos")
          .select(`
            id,
            nome,
            descricao,
            preco,
            imagem_url,
            categoria_id,
            tipo,
            categorias (
              nome
            )
          `)
          .eq("aprovado", true)
          .eq("ativo", true);

        if (categoryId) {
          query = query.eq("categoria_id", categoryId);
        }

        const { data, error } = await query.order("criado_em", { ascending: false });

        if (error) {
          console.error("Erro ao buscar produtos por categoria:", error);
          throw error;
        }

        return data as Product[];
      } else {
        // Unauthenticated users get limited information
        let query = supabase
          .from("produtos")
          .select(`
            id,
            nome,
            categoria_id,
            tipo,
            categorias (
              nome
            )
          `)
          .eq("aprovado", true)
          .eq("ativo", true);

        if (categoryId) {
          query = query.eq("categoria_id", categoryId);
        }

        const { data, error } = await query.order("criado_em", { ascending: false });

        if (error) {
          console.error("Erro ao buscar produtos por categoria:", error);
          throw error;
        }

        // Transform to public interface
        return data.map(item => ({
          ...item,
          descricao: null,
          preco: 0, // Hidden from public
          imagem_url: null,
          rating: undefined,
          reviewCount: undefined
        })) as Product[];
      }
    },
    enabled: !!categoryId,
  });
};